import * as logger from './datadog-logger.js';
import { createFeedbackDialog } from './ct/feedback-dialog-module.js';
import { beginLoad, endLoad } from './popup-load-cancellation.js';

// Below 150ms a load still feels instant, so we paint nothing inside that budget.
const REVEAL_BUDGET_MS = 150;
// Once the placeholder is up, anything shorter than 400ms reads as a flash rather than as loading.
const PLACEHOLDER_MIN_VISIBLE_MS = 400;
const LOAD_ERROR_MESSAGE = 'Der Inhalt konnte nicht geladen werden. Bitte versuche es erneut.';

const PLACEHOLDER_HTML = `
<div class="popup_placeholder" role="status" data-cy="popup-placeholder">
  <span class="sr_only">Wird geladen …</span>
  <span class="popup_placeholder__bar popup_placeholder__bar--title skeleton skeleton--shimmer"></span>
  <span class="popup_placeholder__bar skeleton skeleton--shimmer"></span>
  <span class="popup_placeholder__bar skeleton skeleton--shimmer"></span>
</div>`;

/**
 * @param {string|null|undefined} popupId
 * @returns {string}
 */
function resolvePopupId(popupId) {
  return popupId || 'popup';
}

/**
 * @param {string|null} popupId
 * @returns {HTMLElement|null}
 */
function getPopupElement(popupId) {
  const id = resolvePopupId(popupId);
  const popup = document.getElementById(id);
  if (!popup) {
    const message = 'Invalid popup id: ' + id;
    logger.logError(message);
  }
  return popup;
}

/**
 * Sets content on the given popup or on a subelement.
 *
 * @param {HTMLElement} popup
 * @param {string|null} content
 * @param {string|null} contentSelector
 */
function setPopupContent(popup, content, contentSelector) {
  if (!content) {
    return;
  }
  const contentElement = getContentElement(popup, contentSelector);
  if (contentElement) {
    contentElement.innerHTML = content;
  }
}

/**
 * @param {HTMLElement} popup
 * @param {string|null} contentSelector
 * @returns {HTMLElement|null}
 */
function getContentElement(popup, contentSelector) {
  return contentSelector ? popup.querySelector(contentSelector) : popup;
}

/**
 * @param {HTMLElement} popup
 * @param {string|null} contentSelector
 */
function clearPopupContent(popup, contentSelector) {
  const contentElement = getContentElement(popup, contentSelector);
  if (contentElement) {
    contentElement.innerHTML = '';
  }
}

/**
 * Must run while the popup is open: a closed dialog is `display: none`, has no scrolling
 * box, and silently discards the write.
 *
 * @param {HTMLElement} popup
 * @param {string|null} contentSelector
 */
function scrollContentToTop(popup, contentSelector) {
  const contentElement = getContentElement(popup, contentSelector);
  if (contentElement) {
    contentElement.scrollTop = 0;
  }
}

/**
 * Prevent touch scrolling inside the popup.
 *
 * @param {HTMLElement} popup
 */
function lockTouchScrollOnPopup(popup) {
  const popupModal = popup.querySelector('.popup_modal');
  if (popupModal && popupModal.dataset.touchScrollLocked !== '1') {
    popupModal.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false });
    // Ensure the listener is attached only once.
    popupModal.dataset.touchScrollLocked = '1';
  }
}

// The feedback dialog stacks on top of another popup and must not hold the page on its own.
const OPEN_POPUP_SELECTOR = 'dialog:modal:not(#js-feedback-dialog), .popup[data-popup-open]';

/**
 * Hold the page scroll while any popup is open, and release it once the last one is gone.
 *
 * `showModal()` makes the page behind a dialog inert for clicks and focus, but not for scrolling, so
 * a dialog needs the lock just as much as a popup built from a plain element. The state is derived
 * from the DOM on every open and close, because the close button and Escape reach neither
 * `hidePopup` nor each other, and a popup can close while another one is still open.
 */
function updateScrollLock() {
  document.body.classList.toggle('has_open_popup', document.querySelector(OPEN_POPUP_SELECTOR) !== null);
}

/**
 * @param {string} popupId
 */
function openPopup(popupId) {
  const popup = getPopupElement(popupId);
  if (!popup) {
    return;
  }

  if (popup instanceof HTMLDialogElement) {
    if (!popup.open) {
      popup.showModal();
    }
  } else {
    popup.dataset.popupOpen = '';
  }

  popup.classList.add('active');
  updateScrollLock();
}

/**
 * Open a popup with the given content.
 *
 * @param {string|null} [content = null] The HTML content to display in the popup.
 * @param {string|null} [popupId = null] The ID of the popup element. Falls back to the default id if not given.
 * @param {string|null} [contentSelector = null] The CSS selector to select a subelement within the popup
 *                                               that should receive the content. If not given, the popup
 *                                               element itself will be used.
 */
function showPopup(content, popupId, contentSelector) {
  const popup = getPopupElement(popupId);
  if (!popup) {
    return;
  }

  setPopupContent(popup, content, contentSelector);
  openPopup(popup.id);
  scrollContentToTop(popup, contentSelector);
  lockTouchScrollOnPopup(popup);
}

/**
 * @param {HTMLElement} popup
 */
function restoreFocusIntoPopup(popup) {
  const focused = document.activeElement;
  if (focused !== popup && popup.contains(focused)) {
    return;
  }

  const autofocusTarget = [...popup.querySelectorAll('[autofocus]')].find(
    (element) => element.getClientRects().length > 0,
  );
  (autofocusTarget ?? popup.querySelector('.js_close_dialog'))?.focus();
}

/**
 * @param {number} milliseconds
 * @returns {Promise<void>}
 */
function delay(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * @param {string} popupId
 * @param {(signal: AbortSignal) => Promise<string>} loadContent
 * @param {{contentSelector?: string|null, placeholder?: string, onError?: ((error: Error) => void)|null}} [options]
 * @returns {Promise<string|null>} The content that was shown, or null when none was.
 */
async function showPopupAsync(
  popupId,
  loadContent,
  { contentSelector = null, placeholder = PLACEHOLDER_HTML, onError = null } = {},
) {
  const popup = getPopupElement(popupId);
  if (!popup) {
    return null;
  }

  const controller = beginLoad(popup);

  let placeholderShownAt = null;
  const revealPlaceholder = setTimeout(() => {
    placeholderShownAt = performance.now();
    showPopup(placeholder, popup.id, contentSelector);
  }, REVEAL_BUDGET_MS);

  try {
    const content = await loadContent(controller.signal);
    clearTimeout(revealPlaceholder);
    if (controller.signal.aborted) {
      return null;
    }
    if (!content) {
      const message = 'Empty content for popup ' + popup.id;
      logger.logError(message, { popupId: popup.id });
      throw new Error(message);
    }

    if (placeholderShownAt !== null) {
      await delay(Math.max(0, PLACEHOLDER_MIN_VISIBLE_MS - (performance.now() - placeholderShownAt)));
      if (controller.signal.aborted) {
        return null;
      }
    }

    showPopup(content, popup.id, contentSelector);
    if (placeholderShownAt !== null) {
      restoreFocusIntoPopup(popup);
    }
    return content;
  } catch (error) {
    clearTimeout(revealPlaceholder);
    if (error.name === 'AbortError') {
      return null;
    }
    if (placeholderShownAt !== null) {
      clearPopupContent(popup, contentSelector);
      hidePopup(popup.id);
    }
    if (onError) {
      onError(error);
    } else {
      createFeedbackDialog('feedback_error', LOAD_ERROR_MESSAGE);
    }
    return null;
  } finally {
    endLoad(popup, controller);
  }
}

/**
 * @param {string|null} [popupId = null] The ID of the popup element. Falls back to the default id if not given.
 */
function hidePopup(popupId) {
  const popup = getPopupElement(popupId);
  if (!popup) {
    return;
  }

  if (popup instanceof HTMLDialogElement) {
    if (popup.open) {
      popup.close();
    }
  } else {
    delete popup.dataset.popupOpen;
  }

  popup.classList.remove('active');
  updateScrollLock();
}

export { PLACEHOLDER_HTML, openPopup, showPopup, showPopupAsync, hidePopup, updateScrollLock };
